import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import RouteContainer from '../../../common/styles/RouteContainer'
import RouteTitle from '../../../common/RouteTitle/RouteTitle'
import booksitoutIcon from '../../../config/BooksitoutIcon'
import useLoginStore from '../../login/useLoginStore'
import RouteTitleConfig from '../../../config/RouteTitleConfig'
import Login from '../../../common/Login'
import CardBodyContentContainer from '../../../common/styles/CardBodyContentContainer'
import RowSpacer from '../../../common/styles/RowSpacer'
import useMineBookList from './useMineBookList'
import BookMineListRange from './BookMineListRange'
import InfiniteScroll from 'react-infinite-scroll-component'
import BookResponse from '../BookResponse'
import BookMineCard from './BookMineCard'
import BookMineRangeSelector from './BookMineRangeSelector'
import useUrlQuery from '../../../common/hooks/useUrlQuery'
import { useSearchParams } from 'react-router-dom'
import BookMineLoadingCard from './BookMineLoadingCard'
import BooksByYear from './BooksByYear'
import BookMineGoalCard from './BookMineGoalCard'
import BookMineDoneCard from './BookMineDoneCard'
import useHtmlTitle from '../../../common/useHtmlTitle'
import { BooksitoutServer } from '../../../config/BooksitoutServer'
import NoContent from '../../../common/NoContent'
import useSearchQuery from '../../../common/hooks/useSearchQuery'
import useBookSearchSettings from '../../search/bookSource/hooks/useBookSearchSettings'

const BookMineRoute = () => {
	useHtmlTitle('내 서재')

	const isLoggedIn = useLoginStore(state => state.isLoggedIn())

	return (
		<RouteContainer>
			<RouteTitle icon={<booksitoutIcon.book />} title={'내 서재'} subTitle={'내가 등록한 책을 확인하고 관리할 수 있어요'} currentKey={'mine'} buttons={RouteTitleConfig.Book} rightUi={undefined} />

			{isLoggedIn ? <YesLoggedInCase /> : <NoLoggedInCase />}
		</RouteContainer>
	)
}

interface ReadingSourceFilter {
	provider: string
	label: string
	imageUrl: string | null
}

const DEFAULT_SOURCES: ReadingSourceFilter[] = [
	{ provider: 'BOUGHT', label: '구매 완료', imageUrl: null },
	{ provider: 'SHOULD_BUY_NEW', label: '새로 사야 함', imageUrl: null },
	{ provider: 'NONE', label: '미지정', imageUrl: null },
]

const mergeDefaultSources = (sources: ReadingSourceFilter[]): ReadingSourceFilter[] => {
	const existing = new Set(sources.map(s => s.provider))
	return [...sources, ...DEFAULT_SOURCES.filter(d => !existing.has(d.provider))]
}

const sortReadingSources = (sources: ReadingSourceFilter[]): ReadingSourceFilter[] => {
	const priority = (s: ReadingSourceFilter) => {
		if (s.provider === 'BOUGHT') return 0
		if (s.provider === 'SHOULD_BUY_NEW') return 1
		if (s.provider === 'NONE') return 2
		if (s.imageUrl) return 3
		return 4
	}
	return [...sources].sort((a, b) => priority(a) - priority(b))
}

const YesLoggedInCase = () => {
	const range = (useUrlQuery('range') ?? 'READING') as BookMineListRange
	const [searchParams, setSearchParams] = useSearchParams()
	const sourceFilter = searchParams.get('reading-source')
	const sourceFilters = sourceFilter ? [sourceFilter] : []
	const { query, setQuery, dQuery } = useSearchQuery('', 300)
	const [refreshKey, setRefreshKey] = useState(0)
	const [isLoading, bookList, bookListByYear, paging, totalCount] = useMineBookList(range, dQuery, sourceFilters, refreshKey)

	const handleReadingSourceChange = useCallback(() => {
		if (sourceFilter) {
			setRefreshKey(prev => prev + 1)
		}
	}, [sourceFilter])

	const setSourceFilter = useCallback((provider: string | null) => {
		setSearchParams(prev => {
			if (provider) {
				prev.set('reading-source', provider)
			} else {
				prev.delete('reading-source')
			}
			return prev
		}, { replace: true })
	}, [setSearchParams])
	const [settings] = useBookSearchSettings()
	const isReadingSourceEnabled = settings?.isReadingSourceEnabled ?? false
	const showFilterBadges = settings === null || isReadingSourceEnabled
	const searchInputRef = useRef<HTMLInputElement>(null)

	const [myReadingSources, setMyReadingSources] = useState<ReadingSourceFilter[]>([])
	useEffect(() => {
		if (isReadingSourceEnabled) {
			BooksitoutServer.get('/v1/book/my-reading-sources')
				.then(res => setMyReadingSources(res.data))
				.catch(() => {})
		}
	}, [isReadingSourceEnabled])

	const handleKeyDown = useCallback((e: KeyboardEvent) => {
		if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
			e.preventDefault()
			searchInputRef.current?.focus()
		}
	}, [])

	useEffect(() => {
		document.addEventListener('keydown', handleKeyDown)
		return () => document.removeEventListener('keydown', handleKeyDown)
	}, [handleKeyDown])

	return (
		<>
			<RowSpacer />

			<div className="row">
				<div className="col-12 col-md-6">
					<BookMineRangeSelector />
				</div>

				<div className="col-12 col-md-6">
					<BookMineGoalCard />
				</div>
			</div>

			<RowSpacer size={10} />
			<SearchContainer>
				<booksitoutIcon.search color="gray" />
				<SearchInput ref={searchInputRef} type="text" placeholder="책 제목 또는 저자로 검색" value={query} onChange={e => setQuery(e.target.value)} autoComplete="off" />
				{query && (
					<ClearButton onClick={() => { setQuery(''); searchInputRef.current?.focus() }}>
						<booksitoutIcon.xmark />
					</ClearButton>
				)}
			</SearchContainer>

			{showFilterBadges && (
				<FilterContainer>
					<FilterBadge
						$isSelected={sourceFilter === null}
						$isDisabled={false}
						onClick={() => setSourceFilter(null)}
					>
						전체
					</FilterBadge>
					{sortReadingSources(mergeDefaultSources(myReadingSources)).map(source => {
						const exists = source.provider === 'NONE' ? isReadingSourceEnabled : myReadingSources.some(s => s.provider === source.provider)
						const isSelected = sourceFilter === source.provider
						return (
							<FilterBadge
								key={source.provider}
								$isSelected={isSelected}
								$isDisabled={!exists}
								onClick={exists ? () => setSourceFilter(isSelected ? null : source.provider) : undefined}
							>
								{source.provider === 'BOUGHT' ? (
									<booksitoutIcon.bought size={14} color={isSelected ? 'white' : 'var(--primary-color)'} />
								) : source.provider === 'SHOULD_BUY_NEW' ? (
									<booksitoutIcon.shouldBuy size={14} color={isSelected ? 'white' : 'var(--danger)'} />
								) : source.provider === 'NONE' ? (
									<booksitoutIcon.plus size={18} color={isSelected ? 'white' : 'var(--sub-text-color)'} />
								) : source.imageUrl ? (
									<FilterIcon src={source.imageUrl} />
								) : null}
								{source.label}
							</FilterBadge>
						)
					})}
				</FilterContainer>
			)}

			{range === BookMineListRange.READING ? (
				<BookReadingList query={dQuery} isReadingSourceEnabled={isReadingSourceEnabled} readingSourceProvider={sourceFilter} onReadingSourceChange={handleReadingSourceChange} />
			) : isLoading || query !== dQuery ? (
				<Loading />
			) : (
				<InfiniteScroll loader={<Loading topMargin={10} />} next={paging.fetchNext} hasMore={paging.hasMore} dataLength={bookList.length} className="overflow-hidden">
					<RowSpacer size={60} />
					<Row>
						{bookList.length === 0 ? (
							<>
								<RowSpacer size={50} />
								<NoContent message={range == 'GIVEUP' ? '포기한 책이 없어요' : range == 'DONE' ? '다 읽은 책이 없어요' : '책이 없어요'} icon={booksitoutIcon.noBook} />
								<RowSpacer size={75} />
							</>
						) : range === BookMineListRange.DONE ? (
							<BookDoneList bookListByYear={bookListByYear} />
						) : (
							bookList.map(book => (
								<Col key={book.id}>
									<BookMineCard book={book} isReadingSourceEnabled={isReadingSourceEnabled} onReadingSourceChange={handleReadingSourceChange} />
								</Col>
							))
						)}
					</Row>
				</InfiniteScroll>
			)}
		</>
	)
}

const BookReadingList: React.FC<{ query: string; isReadingSourceEnabled: boolean; readingSourceProvider: string | null; onReadingSourceChange: () => void }> = ({ query, isReadingSourceEnabled, readingSourceProvider, onReadingSourceChange }) => {
	const size = 24
	const queryParam = query ? `&q=${encodeURIComponent(query)}` : ''
	const sourceParam = readingSourceProvider ? `&readingSourceProviders=${readingSourceProvider}` : ''

	const [readingBooks, setReadingBooks] = useState<BookResponse[]>([])
	const [readingCount, setReadingCount] = useState<number | null>(null)
	const [readingLoading, setReadingLoading] = useState(true)
	const [readingPage, setReadingPage] = useState(1)
	const [readingIsLast, setReadingIsLast] = useState(false)

	const [notStartedBooks, setNotStartedBooks] = useState<BookResponse[]>([])
	const [notStartedCount, setNotStartedCount] = useState<number | null>(null)
	const [notStartedLoading, setNotStartedLoading] = useState(true)
	const [notStartedPage, setNotStartedPage] = useState(1)
	const [notStartedIsLast, setNotStartedIsLast] = useState(false)
	const [readingFetchKey, setReadingFetchKey] = useState(0)

	const handleReadingSourceChange = useCallback(() => {
		if (readingSourceProvider) {
			setReadingPage(1)
			setReadingBooks([])
			setReadingLoading(true)
			setNotStartedPage(1)
			setNotStartedBooks([])
			setNotStartedLoading(true)
			setReadingFetchKey(prev => prev + 1)
		}
		onReadingSourceChange()
	}, [readingSourceProvider, onReadingSourceChange])

	useEffect(() => {
		setReadingPage(1)
		setReadingBooks([])
		setReadingLoading(true)
		setNotStartedPage(1)
		setNotStartedBooks([])
		setNotStartedLoading(true)
	}, [query, readingSourceProvider])

	useEffect(() => {
		BooksitoutServer.get(`/v1/book/my-book?range=ONLY_READING&page=${readingPage}&size=${size}${queryParam}${sourceParam}`)
			.then(res => {
				setReadingBooks(prev => readingPage === 1 ? res.data.content : [...prev, ...res.data.content])
				setReadingCount(res.data.totalCount ?? 0)
				setReadingIsLast(res.data.isLast)
			})
			.finally(() => setReadingLoading(false))
	}, [readingPage, query, readingFetchKey, readingSourceProvider])

	useEffect(() => {
		if (!readingLoading) {
			BooksitoutServer.get(`/v1/book/my-book?range=NOT_STARTED&page=${notStartedPage}&size=${size}${queryParam}${sourceParam}`)
				.then(res => {
					setNotStartedBooks(prev => notStartedPage === 1 ? res.data.content : [...prev, ...res.data.content])
					setNotStartedCount(res.data.totalCount ?? 0)
					setNotStartedIsLast(res.data.isLast)
				})
				.finally(() => setNotStartedLoading(false))
		}
	}, [readingLoading, notStartedPage, query, readingFetchKey, readingSourceProvider])

	const hasMore = !readingIsLast || !notStartedIsLast
	const fetchNext = () => {
		if (!readingIsLast) {
			setReadingPage(prev => prev + 1)
		} else if (!notStartedIsLast) {
			setNotStartedPage(prev => prev + 1)
		}
	}

	if (readingLoading) return (
		<>
			<RowSpacer size={60} />
			<Row>
				<SectionHeader>
					<SectionLabel>시작함</SectionLabel>
				</SectionHeader>
				{Array.from({ length: 12 }).map((_, i) => (
					<Col key={i}>
						<BookMineLoadingCard />
					</Col>
				))}
			</Row>
		</>
	)

	if (readingBooks.length === 0 && notStartedBooks.length === 0 && !notStartedLoading) {
		return (
			<>
				<RowSpacer size={50} />
				<NoContent message={'읽고 있는 책이 없어요'} icon={booksitoutIcon.noBook} />
				<RowSpacer size={75} />
			</>
		)
	}

	return (
		<InfiniteScroll loader={<Loading topMargin={10} />} next={fetchNext} hasMore={hasMore} dataLength={readingBooks.length + notStartedBooks.length} className="overflow-hidden">
			<RowSpacer size={60} />
			<Row>
				{readingBooks.length > 0 && (
					<>
						<SectionHeader>
							<SectionLabel>시작함</SectionLabel>
							<SectionCount className="text-book">{readingCount != null ? `${readingCount}권` : '\u00A0'}</SectionCount>
						</SectionHeader>
						{readingBooks.map(book => (
							<Col key={book.id}>
								<BookMineCard book={book} isReadingSourceEnabled={isReadingSourceEnabled} onReadingSourceChange={handleReadingSourceChange} />
							</Col>
						))}
					</>
				)}

				{notStartedLoading ? (
					<Loading topMargin={10} />
				) : notStartedBooks.length > 0 && (
					<>
						<SectionHeader className={readingBooks.length > 0 ? 'pt-5' : ''}>
							<SectionLabel>시작 전</SectionLabel>
							<SectionCount className="text-book">{notStartedCount != null ? `${notStartedCount}권` : '\u00A0'}</SectionCount>
						</SectionHeader>
						{notStartedBooks.map(book => (
							<Col key={book.id}>
								<BookMineCard book={book} isReadingSourceEnabled={isReadingSourceEnabled} onReadingSourceChange={handleReadingSourceChange} />
							</Col>
						))}
					</>
				)}
			</Row>
		</InfiniteScroll>
	)
}

const SectionHeader = styled.div`
	display: flex;
	justify-content: space-between;
	width: 100%;
	padding-bottom: 12px;
	padding-left: 8px;
	padding-right: 8px;
`

const SectionLabel = styled.h3`
	color: var(--text-color);
	margin: 0;
`

const SectionCount = styled.h5`
	font-weight: bold;
	padding-left: 10px;
	align-self: flex-end;
	padding-bottom: 3px;
`

interface Props {
	bookListByYear: BooksByYear
}

const BookDoneList: React.FC<Props> = ({ bookListByYear }) => {
	const sortedYears = Object.keys(bookListByYear)
		.map(Number)
		.sort((a, b) => b - a)

	const [yearToBookCount, setYearToBookCount] = useState<Map<number, number>>(new Map())
	useEffect(() => {
		const currentYear = new Date().getFullYear()
		const years = Array.from({ length: 10 }, (_, i) => currentYear - i).join(',')

		BooksitoutServer.get(`/v1/statistics/book-counts?years=${years}`).then(res => {
			const bookCountsArray = Object.entries(res.data).map(([year, count]) => [Number(year), count])
			setYearToBookCount(new Map(bookCountsArray as [number, number][]))
		})
	}, [])

	return (
		<>
			{sortedYears.map(year => {
				const books = bookListByYear[year]
				const bookCount = yearToBookCount.get(year)?.toString() ?? '-'

				return (
					<>
						<YearHeader className={sortedYears[0] !== year ? 'pt-5' : ''}>
							<h3 className={'text-start pb-3 ms-2'} style={{ color: 'var(--text-color)' }}>
								{year}년
							</h3>
							<BookCountContainer className={'text-book'}>{bookCount}권</BookCountContainer>
						</YearHeader>

						{books.map(book => (
							<Col key={book.id}>
								<BookMineDoneCard book={book} />
							</Col>
						))}
					</>
				)
			})}
		</>
	)
}

const YearHeader = styled.div`
	display: flex;
	justify-content: space-between;
	width: 100%;
	padding-right: 8px;
`

const BookCountContainer = styled.h5`
	font-weight: bold;
	align-self: flex-end;
	padding-bottom: 15px;
`

const Loading: React.FC<{ topMargin?: number }> = ({ topMargin = 60 }) => {
	return (
		<>
			<RowSpacer size={topMargin} />
			<Row>
				{Array.from({ length: 12 }).map((_, index) => {
					return (
						<Col key={index}>
							<BookMineLoadingCard />
						</Col>
					)
				})}
			</Row>
		</>
	)
}

const NoLoggedInCase = () => {
	return (
		<>
			<RowSpacer/>
			<CardBodyContentContainer style={{ paddingTop: '40px', paddingBottom: '40px' }}>
				<Login message={'로그인해 내 서재 보기'} />
			</CardBodyContentContainer>
		</>
	)
}

const SearchContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	min-height: 48px;
	padding: 8px 16px;
	border-radius: 16px;
	background-color: var(--search-field-bg-color);
	transition: box-shadow 0.2s ease, background-color 0.2s ease;

	@media (max-width: 768px) {
		margin-top: 10px;
	}

	&:focus-within {
		box-shadow: none;
		background-color: var(--input-focus-bg-color);
	}
`

const SearchInput = styled.input`
	flex: 1;
	min-width: 0;
	border: none;
	font-size: 16px;
	line-height: 1.25;
	background-color: transparent;
	color: var(--text-color);
	outline: none;
	caret-color: var(--primary-color);

	&::placeholder {
		color: var(--sub-text-color);
	}
`

const ClearButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	background: none;
	border: none;
	cursor: pointer;
	padding: 0;
	color: var(--sub-text-color);
	flex-shrink: 0;
	font-size: 18px;

	&:hover {
		color: var(--text-color);
	}
`

const FilterContainer = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	margin-top: 10px;
`

const FilterBadge = styled.div<{ $isSelected: boolean; $isDisabled: boolean }>`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	padding: 6px 12px;
	border-radius: 20px;
	font-size: 0.8rem;
	font-weight: 600;
	cursor: ${({ $isDisabled }) => $isDisabled ? 'default' : 'pointer'};
	opacity: ${({ $isDisabled }) => $isDisabled ? 0.4 : 1};
	background-color: ${({ $isSelected }) => $isSelected ? 'var(--primary-color)' : 'var(--search-field-bg-color)'};
	color: ${({ $isSelected }) => $isSelected ? 'white' : 'var(--text-color)'};
	transition: background-color 0.2s ease;

	&:hover {
		opacity: ${({ $isDisabled }) => $isDisabled ? 0.4 : 0.85};
	}
`

const FilterIcon = styled.img`
	width: 16px;
	height: 16px;
	border-radius: 4px;
`

const Row = styled.div.attrs({
	className: 'row row-eq-height ps-2 pe-2',
})``

const Col = styled.div.attrs({
	className: 'col-6 col-sm-4 col-md-3 col-xl-2',
})`
	height: 100%;
`

export default BookMineRoute
